import {
  createContext,
  useReducer,
  useEffect,
  useContext,
  Dispatch,
} from 'react'
import { getCohortsApi } from 'src/api/cohorts'
import { GlobalContext } from '../global'
import { GlobalStoreContext } from '../globalStore'
import { transformUTC0ToLocalTime, numFixed } from 'src/utils'
import dayjs from 'dayjs'

type Props = {
  gameOption: any
  countryOption: any
  gameListObject: any
  countryListObject: any
  game: any
  country: any
  time: any
  platform: string
  loading: boolean
  thirtyLoading: boolean
  userData: any
  retentionsAverage: any
  thirtyUserData: any
  genre: string
  retentionTrend: any
  genreList: any
  firstLoad: boolean
  resetIndex: number
  newChartType: string
}

type Action = {
  type: string
  payload?: any
}

type Reducer = (prevState: Props, action: Action) => Props

const initialState: Props = {
  gameOption: [],
  gameListObject: {},
  countryOption: [],
  countryListObject: {},
  game: '',
  country: '',
  time: dayjs(new Date()).add(-30, 'day'),
  platform: '',
  loading: false,
  thirtyLoading: false,
  userData: [],
  retentionsAverage: [],
  thirtyUserData: [],
  genre: '',
  retentionTrend: {
    trend: '-',
    value: '-',
  },
  genreList: [],
  firstLoad: true,
  resetIndex: 0,
  newChartType: 'Graph',
}
const reducer: Reducer = (prevState: Props, action: Action): Props => {
  switch (action.type) {
    case 'retentionTrend':
      const disabled = action.payload?.tabDisabled || false
      const untilTime = action.payload?.untilTime || null
      if (!action.payload?.num) {
        return {
          ...prevState,
          retentionTrend: {
            value: '0%',
            trend: 'up',
            disabled,
            untilTime,
          },
        }
      }
      let trend = 'up'
      if (action.payload >= 0) {
        trend = 'up'
      } else {
        trend = 'down'
      }
      const retentionTrend = {
        value: numFixed(Math.abs(action.payload.num || 0)) + '%',
        trend,
        disabled,
        untilTime,
      }
      return { ...prevState, retentionTrend }
    case 'time':
    case 'country':
    case 'game':
    case 'gameOption':
    case 'gameListObject':
    case 'countryOption':
    case 'countryListObject':
    case 'platform':
    case 'loading':
    case 'thirtyLoading':
    case 'userData':
    case 'retentionsAverage':
    case 'thirtyUserData':
    case 'genre':
    case 'genreList':
    case 'firstLoad':
    case 'resetIndex':
    case 'newChartType':
      return { ...prevState, [action.type]: action.payload }
    default:
      throw new Error(`Unhandled action type: ${action.type}`)
  }
}

type Context = {
  state: Props
  dispatch: Dispatch<Action>
  search: (genre?: string) => void
}

export const CohortsContext = createContext<Context>({
  state: initialState,
  dispatch: (action: Action) => {},
  search: () => {},
})
export const CohortsContextProvider = ({
  children,
}: JSX.ElementChildrenAttribute): JSX.Element => {
  const [state, dispatch] = useReducer<Reducer>(reducer, initialState)
  const { state: globalState, errorCatch } = useContext(GlobalContext)
  const { state: globalStoreState, dispatch: globalStoreDispatch } =
    useContext(GlobalStoreContext)

  // handlers

  // effects
  // 获取总的游戏列表
  useEffect(() => {
    dispatch({ type: 'gameListObject', payload: globalState.gameListObject })
    dispatch({ type: 'gameOption', payload: globalState.gameOption })
    if (globalState.gameOption.length) {
      dispatch({ type: 'game', payload: globalState.gameOption[0] })
    }
    // eslint-disable-next-line
  }, [globalState.gameListObject])
  // 获取国家列表
  useEffect(() => {
    dispatch({
      type: 'countryListObject',
      payload: globalState.countryListObject,
    })
    dispatch({ type: 'countryOption', payload: globalState.countryOption })
    // eslint-disable-next-line
  }, [globalState.countryListObject])
  const getCohorts = () => {
    const params = {
      app_key: state.game.app_key,
      country_code: state.country.code,
      from_date: transformUTC0ToLocalTime(state.time, 'YYYY-MM-DD'),
      to_date: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
      duration: 30,
    }
    dispatch({ type: 'loading', payload: true })
    getCohortsApi({ ...params, type: 'retention' })
      .then((res: any) => {
        dispatch({ type: 'userData', payload: res.data.data })
        dispatch({ type: 'retentionsAverage', payload: res.data.average })
        globalStoreDispatch({
          type: 'retentionData',
          payload: {
            userData: res.data.data,
            retentionsAverage: res.data.average,
          },
        })
      })
      .catch((err: any) => {
        errorCatch(err)
        globalStoreDispatch({
          type: 'retentionData',
          payload: {
            userData: [],
            retentionsAverage: [],
          },
        })
      })
      .finally(() => {
        dispatch({ type: 'loading', payload: false })
      })
  }
  const getThirtyCohorts = (genre?: string) => {
    const params: any = {
      app_key: state.game.app_key,
      country_code: state.country.code,
      from_date: transformUTC0ToLocalTime(state.time, 'YYYY-MM-DD'),
      to_date: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
      duration: 30,
      genre: genre || state.genre,
      benchmark: true,
    }
    globalStoreDispatch({
      type: 'retentionData',
      payload: {
        filter: {
          game: state.game,
          country: state.country,
          genre: genre || state.genre,
          time: state.time,
        },
      },
    })
    // 存储retention数据
    dispatch({ type: 'thirtyLoading', payload: true })
    getCohortsApi({ ...params, type: 'retention' })
      .then((res: any) => {
        dispatch({
          type: 'retentionTrend',
          payload: {
            num: res.data.relative_change || 0,
          },
        })
        dispatch({ type: 'thirtyUserData', payload: { ...res.data } })
        globalStoreDispatch({
          type: 'retentionData',
          payload: {
            retentionTrend: {
              num: res.data.relative_change || 0,
            },
            thirtyUserData: { ...res.data },
          },
        })
      })
      .catch((err: any) => {
        if (
          err?.response?.status === 503 &&
          err?.response?.data?.code === 'trino_migration'
        ) {
          let untilTime = null
          if (err?.response?.data?.extra?.after) {
            untilTime = transformUTC0ToLocalTime(
              err?.response?.data?.extra?.after,
              'Do MMM YYYY hh:mm a'
            )
          }
          dispatch({
            type: 'retentionTrend',
            payload: { num: 0, tabDisabled: true, untilTime },
          })
          globalStoreDispatch({
            type: 'retentionData',
            payload: {
              retentionTrend: { num: 0, tabDisabled: true, untilTime },
              thirtyUserData: [],
            },
          })
        } else {
          errorCatch(err)
          globalStoreDispatch({
            type: 'retentionData',
            payload: {
              retentionTrend: { num: 0 },
              thirtyUserData: [],
            },
          })
        }
      })
      .finally(() => {
        dispatch({ type: 'thirtyLoading', payload: false })
      })
  }
  const search = (genre?: string) => {
    if (state?.game.app_key && (genre || state.genre)) {
      getCohorts()
      getThirtyCohorts(genre)
    }
  }

  useEffect(() => {
    let temp: any = []
    if (state?.game?.platform && globalState?.genreList[state.game.platform]) {
      const list = globalState.genreList[state.game.platform]
      temp = list.map((item: any) => {
        return {
          value: item,
          label: item,
        }
      })
      // firstLoad防止game变化时每次都取filter中的内容
      if (state.firstLoad && globalStoreState.retentionData.filter) {
        Object.keys(globalStoreState.retentionData.filter).forEach(
          (item: string) => {
            dispatch({
              type: item,
              payload: globalStoreState.retentionData.filter[item],
            })
          }
        )
        Object.keys(globalStoreState.retentionData)
          .filter((item: string) => item !== 'filter')
          .forEach((item: string) => {
            dispatch({
              type: item,
              payload: globalStoreState.retentionData[item],
            })
          })
      } else {
        dispatch({
          type: 'genre',
          payload: list[0],
        })
        search(list[0])
      }
      setTimeout(() => {
        dispatch({
          type: 'firstLoad',
          payload: false,
        })
      })
    }
    dispatch({ type: 'genreList', payload: temp })
    // eslint-disable-next-line
  }, [globalState.genreList, state.game])
  useEffect(() => {
    if (state.resetIndex) {
      search()
    }
    // eslint-disable-next-line
  }, [state.resetIndex])
  // returns
  return (
    <CohortsContext.Provider
      value={{
        state,
        dispatch,
        search,
      }}
    >
      {children}
    </CohortsContext.Provider>
  )
}
